import React, { useEffect, useRef, useState } from 'react';
import { Download, FileText, Upload, Trash2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './ConfirmDialog';

/**
 * DocumentDownload — Renders a download card for an admin-uploaded PDF.
 * Admin can upload / replace / remove the PDF from the same card.
 *
 * Props:
 *  - documentKey: string   Unique key for this document (e.g. 'rcia_form')
 *  - title: string         Display name (e.g. 'RCIA Registration Form')
 *  - description: string   Short blurb explaining what the document is
 *  - fallback: string      Optional message shown to public visitors when no PDF is uploaded
 *  - accept: string        MIME/extension accept string (default 'application/pdf,.pdf')
 */
const DocumentDownload = ({
  documentKey,
  title,
  description,
  fallback = '',
  accept = 'application/pdf,.pdf',
}) => {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [doc, setDoc] = useState(null);      // {key, filename, original_name, size, ...}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.getDocument(documentKey);
      setDoc(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setDoc(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [documentKey]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.uploadDocument(documentKey, fd);
      setDoc(data);
      toast({ title: doc ? 'Document replaced' : 'Document uploaded', description: `${data.original_name} (${data.size})` });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    try {
      await api.deleteDocument(documentKey);
      setDoc(null);
      toast({ title: 'Document removed' });
    } catch (err) {
      toast({ title: 'Could not remove', description: err?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setConfirmRemove(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#e9e0cc] p-6 flex items-center justify-center text-sm text-[#78716c]">
        Loading…
      </div>
    );
  }

  // Hidden file input used by both upload and replace buttons
  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={accept}
      onChange={handleFileChange}
      className="hidden"
    />
  );

  if (doc) {
    return (
      <>
        <div className="bg-white border border-[#e9e0cc] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center flex-shrink-0">
              <FileText size={20} />
            </div>
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] mb-1">PDF Document</p>
              <h3 className="font-display text-xl text-[#2a1a45]">{title}</h3>
              {description && <p className="text-sm text-[#5a534a] mt-1 max-w-md">{description}</p>}
              <p className="text-xs text-[#78716c] mt-2">{doc.original_name} — {doc.size}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={api.documentDownloadUrl(documentKey)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] whitespace-nowrap"
            >
              <Download size={16} /> Download {title.split(' ')[0]} Form
            </a>
            {isAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#3a2359] text-[#3a2359] text-xs uppercase tracking-wider hover:bg-[#3a2359] hover:text-white disabled:opacity-60"
                >
                  <Upload size={13} /> {uploading ? 'Replacing…' : 'Replace'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemove(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#a63a3a] text-[#a63a3a] text-xs uppercase tracking-wider hover:bg-[#a63a3a] hover:text-white"
                >
                  <Trash2 size={13} /> Remove
                </button>
              </>
            )}
          </div>
          {hiddenInput}
        </div>
        <ConfirmDialog
          open={confirmRemove}
          title="Remove this document?"
          message={`${doc.original_name} will be permanently removed and visitors will no longer be able to download it.`}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={handleRemove}
        />
      </>
    );
  }

  // No document uploaded yet
  if (isAdmin) {
    return (
      <div className="bg-[#f2ead6]/40 border-2 border-dashed border-[#e9e0cc] p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-white text-[#a68a5b] flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} />
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] mb-1">Admin: No PDF Uploaded Yet</p>
            <h3 className="font-display text-xl text-[#2a1a45]">{title}</h3>
            {description && <p className="text-sm text-[#5a534a] mt-1 max-w-md">{description}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] whitespace-nowrap disabled:opacity-60"
        >
          <Upload size={16} /> {uploading ? 'Uploading…' : `Upload PDF`}
        </button>
        {hiddenInput}
      </div>
    );
  }

  // Public visitor, no document uploaded → optional fallback
  if (!fallback) return null;
  return (
    <div className="bg-[#f2ead6]/40 border border-[#e9e0cc] p-6">
      <p className="text-sm text-[#5a534a]">{fallback}</p>
    </div>
  );
};

export default DocumentDownload;
